import { useEffect, useState, useCallback, useRef } from "react";
import "./reviews.css";

const reviews = [
  {
    id: 1,
    text: "Maggi loves Kubra and Zack and gets so sad if she goes too long without seeing them. After three years of walking Maggi each week and keeping her when we go out of town, we really view Kubra and Zack as part of family now. Our lives are so much easier having people like them whom we can trust to help us take care of our dog daughter!",
    author: "West H.",
    stars: 5,
  },
  {
    id: 2,
    text: "Kubra and Zack are the absolutely best. Reliable, friendly, affordable, and so good with our energetic little pup. We don't know what we'd do without them.",
    author: "T.C. L.",
    stars: 5,
  },
  {
    id: 3,
    text: "We are so grateful to have Kubra and Zack in our lives. After adopting our 5-year-old Beagle, Melo, they have walked him during busy workweeks, cared for him overnight in both their home and ours, and treated him with so much love, truly like their own. They are not only exceptional caregivers but also incredibly knowledgeable about dog training; Kubra even helped me leash-train Melo. I always feel  confident and at ease knowing Melo is safe and happy with Kubra and Zack. I recommend them to any pet owner.",
    author: "Tara P.",
    stars: 5,
  },
  {
    id: 4,
    text: "You know you've found the best dog walkers when your dog genuinely gets excited every time he sees them at the park. Aside from our dog-loving Kubra and Zack, we do too! The level of trust we have in both of them is unmatched. They're reliable, dependable, and sometimes even give Porter extra walking time just because it's a nice day out. We've tried a few different dog walkers in the area, and hands down, they're the best!",
    author: "Alyse C.",
    stars: 5,
  },
  {
    id: 5,
    text: "Kubra and Zack have become such an integral part of our Jersey City 'village' that we genuinely don't feel like we can even consider leaving the neighborhood for years because we don't know what we would do without them. We love our pup Poppy so much and struggle with so much anxiety about leaving her, but Kubra and Zack love her like she's their family. They have seamlessly and thoughtfully handled an emergency vet visit when we were out of town, and they shower her with so much love and care that we feel bad if it has been too long without Poppy seeing them. We recommend Kubra and Zack to anyone we know with a furry loved one in a heartbeat because choosing them to be part of Poppy's and our lives is one of the best decisions we've ever made.",
    author: "Alisha M.",
    stars: 5,
  },
];

export default function ReviewsCarousel() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);
  const touchStartX = useRef(null);
  const mouseStartX = useRef(null);
  const isDragging = useRef(false);
  const pausedRef = useRef(false);

  // Keep ref in sync so the interval callback always sees the latest paused value
  pausedRef.current = paused;

  const resetTimer = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      if (!pausedRef.current) {
        setActive((prev) => (prev + 1) % reviews.length);
      }
    }, 4000);
  }, []);

  const goTo = useCallback(
    (idx) => {
      setActive((idx + reviews.length) % reviews.length);
      resetTimer();
    },
    [resetTimer]
  );

  useEffect(() => {
    resetTimer();
    return () => clearInterval(timerRef.current);
  }, [resetTimer]);

  useEffect(() => {
    const items = document.querySelectorAll(".fade-in-reviews");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );
    items.forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, []);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) goTo(active + (diff > 0 ? 1 : -1));
    touchStartX.current = null;
  };

  const handleMouseDown = (e) => {
    mouseStartX.current = e.clientX;
    isDragging.current = true;
  };

  const handleMouseUp = (e) => {
    if (!isDragging.current) return;
    const diff = mouseStartX.current - e.clientX;
    if (Math.abs(diff) > 40) goTo(active + (diff > 0 ? 1 : -1));
    isDragging.current = false;
  };

  const handleMouseLeave = () => {
    isDragging.current = false;
    setPaused(false);
  };

  return (
    <div className="fade-in-reviews">
      <h1 className="header-reviews fade-in-reviews">
        Happy Dogs, Happy Owners
      </h1>
      <div
        className="carousel-container"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={handleMouseLeave}
        style={{ cursor: "grab", userSelect: "none" }}
      >
        <div className="star-container">
          {[...Array(reviews[active].stars)].map((_, i) => (
            <StarIcon key={i} />
          ))}
        </div>
        <div className="review-content" key={reviews[active].id}>
          <p className="review-text fade-in-2">{reviews[active].text}</p>
          <p className="review-author">— {reviews[active].author}</p>
        </div>
        <Dots
          active={active}
          count={reviews.length}
          goTo={goTo}
          paused={paused}
          onTogglePause={() => setPaused((p) => !p)}
        />
      </div>
    </div>
  );
}

function Dots({ active, count, goTo, paused, onTogglePause }) {
  return (
    <div className="dots-container">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="dot-wrapper"
          onClick={() => goTo(i)}
          style={{ cursor: "pointer" }}
        >
          {active === i ? (
            <>
              <div className="dot is-active-inner"></div>
              <svg className="timer-svg" viewBox="0 0 24 24">
                <circle
                  className="timer-circle"
                  cx="12"
                  cy="12"
                  r="11"
                  style={{ animationPlayState: paused ? "paused" : "running" }}
                />
              </svg>
            </>
          ) : (
            <div className="dot is-inactive"></div>
          )}
        </div>
      ))}

      <button
        className="pause-btn"
        onClick={onTogglePause}
        aria-label={paused ? "Play" : "Pause"}
      >
        {paused ? <PlayIcon /> : <PauseIcon />}
      </button>
    </div>
  );
}

function PauseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <rect x="5" y="3" width="4" height="18" rx="1" />
      <rect x="15" y="3" width="4" height="18" rx="1" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 4l14 8-14 8V4z" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="black"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
    </svg>
  );
}